import { Fragment, type ReactNode } from 'react';
import { SearchResultItem } from '../types';
import { cn } from '@ifixit/ui/lib/utils';

interface HighlightProps {
   attribute: string;
   hit: SearchResultItem;
   isSnippet?: boolean;
   className?: string;
   /**
    * Rendered inside the highlight box, after the text, so it shares the title's
    * line-clamp/wrap flow instead of dropping to its own line. Used to fold the
    * store-offer axis suffix into the title (#55186).
    */
   children?: ReactNode;
}

/**
 * Component for highlighting search matches in text
 * This replicates the functionality from the original highlight.jsx
 */
export function Highlight({
   attribute,
   hit,
   isSnippet = false,
   className,
   children,
}: HighlightProps) {
   // Determine which property to use for highlights
   const highlightProperty = isSnippet ? '_snippetResult' : '_highlightResult';
   const highlights = hit[highlightProperty]?.[attribute];

   if (!highlights) {
      // If no highlights, just return the original text
      // Safe access using proper type assertions with unknown intermediate step
      const attributeValue =
         attribute in hit ? (hit as unknown as Record<string, unknown>)[attribute] : '';
      return (
         <span className={className}>
            {attributeValue as ReactNode}
            {children}
         </span>
      );
   }

   // Parse the highlighted value
   const value = highlights.value || '';
   const parts = [];
   let lastIndex = 0;
   let match;

   // Regular expression to find highlighted parts (wrapped in <em> tags)
   const regex = /<em>(.*?)<\/em>/g;

   while ((match = regex.exec(value)) !== null) {
      const start = match.index;
      const end = regex.lastIndex;

      // Add the text before the highlight
      if (start > lastIndex) {
         parts.push({
            isHighlighted: false,
            value: value.substring(lastIndex, start),
         });
      }

      // Add the highlighted text
      const highlightedText = match[1];
      parts.push({
         isHighlighted: true,
         value: highlightedText,
      });

      lastIndex = end;
   }

   // Add any remaining text after the last highlight
   if (lastIndex < value.length) {
      parts.push({
         isHighlighted: false,
         value: value.substring(lastIndex),
      });
   }

   // If no parts were created (no regex matches), use the whole value
   if (parts.length === 0 && value) {
      parts.push({
         isHighlighted: false,
         value,
      });
   }

   return (
      <span className={cn('break-all line-clamp-1 sm:line-clamp-5', className)}>
         {parts.map((part, index) => {
            // Always highlight parts marked as highlighted
            if (part.isHighlighted) {
               return (
                  <span key={index} className="bg-[#fef9c3]">
                     {part.value}
                  </span>
               );
            }
            return <Fragment key={index}>{part.value}</Fragment>;
         })}
         {children}
      </span>
   );
}
